import React, { useState, useEffect } from 'react';
import Breadcrumbs from '../../components/shared/Breadcrumbs';
import ProductGrid from './ProductGrid';
import AddToWishlistButton from '../wishlists/AddToWishlistButton';
import ProductSpecs from './ProductSpecs';
import YoutubeEmbed from '../../components/shared/YoutubeEmbed';
import { Contributor } from '../../components/shared/Contributor';
import Modal from 'react-responsive-modal';
import { PlayIcon, PlayIconActive, QuestionIcon } from '../../modules/Icons';
import CollectionOwnership from './CollectionOwnership';
import TagContainer from '../shared/TagContainer';
import { Tooltip } from 'react-tooltip';
import GoogleProductData from '../../layout/GoogleProductMetaData';
import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader
import { productMedia } from '../../staticData/ProductMedia';
import TruncatedText from '../shared/TruncatedText';
import { CollectionsShowResponse } from '../../types/api/CollectionsShowTypes';
import {
  CollectionProductMediaResponse,
  CollectionProductMediaLegacyResponse,
  CollectionDemoVideoResponse,
  CollectionElementsFromResponse
} from '../../types/api/CollectionsIndexTypes';
import CustomCarouselWithThumbs from '../../modules/CustomCarouselWithThumbs';
import PriceBlock from './PriceBlock';
import DownloadLimitNotification from '../downloads/DownloadLimitNotification';
import SubscriptionUpgradeModal from '../subscription-upgrade-modal/SubscriptionUpgradeModal';
import { StaticAdContainer } from '../shared/StaticAdContainer';
import LicenseSelectionAssistDialog from '../shared/LicenseSelectionAssistDialog';
import MiniLicenseProfile from '../shared/MiniLicenseProfile';

import InactiveCollectionMarker from '../shared/InactiveCollectionMarker';
import { DividerWithText } from '../../modules/DividerWithText';
import NonSubPriceBlock from './NonSubPriceBlock';
import nonSubscriptionPurchaseHelpers from '../../helpers/nonSubscriptionPurchase';
import Sidebar from '../sidebar/Sidebar';
import PermanentPurchaseOptionsModal from '../permanent-purchase-options-modal/permanentPurchaseOptionsModal';
import RelatedCollectionsSection from './RelatedCollectionsSection';

export default function CollectionPage(props) {
  const { collection: _collection, products, product, ownership: _ownership, subscription, user, auth } = props;
  const { shouldHideAddToCart } = nonSubscriptionPurchaseHelpers;

  const [collection, setCollection] = useState(_collection);
  const [videoModal, setVideoModal] = useState(null);
  const [collectionOwned, setCollectionOwned] = useState(Object.keys(_ownership || {}).length >= products.length);
  const [ownership, setOwnership] = useState(_ownership);
  const [licenseSelectionAssistDialogOpen, setLicenseSelectionAssistDialogOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [openPernamentPaymentOptionsModal, setOpenPernamentPaymentOptionsModal] = useState(false)
  const [showPricing, setShowPricing] = useState<boolean>();
  const [showOwnership, setShowOwnership] = useState<boolean>();

  const hasLicense = !!user?.team?.licensee_profile
  const licenseeProfile = hasLicense ? user?.team?.licensee_profile : null

  const handlePurchaseOptionsClick = () => {
    setOpenPernamentPaymentOptionsModal(true);
  };

  const onClose = () => setOpenPernamentPaymentOptionsModal(false)

  const totalCreditPrice = products.reduce((total, product) => total + product.credit_price, 0);

  useEffect(() => {
    const shouldShowPricing = collection?.free_for_member && !collection?.fully_purchased || !collectionOwned;
    const shouldShowOwnership = !shouldShowPricing || collection?.free_for_member;
    setShowPricing(shouldShowPricing);
    setShowOwnership(shouldShowOwnership);
  }, [
    collection,
    collectionOwned,
  ]);

  const productMediaData = (collection: CollectionsShowResponse) => {
    if (collection.product_media && collection.product_media.length > 0) {
      // New way
      return collection.product_media
        .sort((a, b) => a.position - b.position)
        .map((m: CollectionProductMediaResponse): CollectionProductMediaLegacyResponse => {
          return {
            type: m.role == 'Video' ? 'youtube' : 'image',
            subtype: m.role,
            url: m.role == 'Video' ? m.youtube_link : m.url
          };
        });
    }

    if (productMedia[collection.sku]) {
      // Legacy way
      return productMedia[collection.sku];
    }

    return false;
  };

  const carouselOptions = {
    dots: false,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    arrows: false,
    fade: true,
    speed: 300,
    overflow: 'hidden'
  };

  return (
    <>
      <GoogleProductData
        price={0}
        name={collection.name}
        image={collection.media.image}
        description={collection.description.replace(/<[^>]*>/g, '' + ' - Only available through subscription')}
        sku={collection.sku}
        productUrl={`https://www.actionvfx.com/collections/${collection.permalink}`}
      />
      <div className="text-white bg-a-darker-gray">
        <div className="flex ">
          <div
            id="collection-page"
            className="w-full md:w-[calc(100%-380px)] lg:w-[calc(100%-400px)] 2xl:w-[calc(100%-440px)] bg-[#252A2D]">
            <Breadcrumbs
              className="hidden px-4 py-8 md:block md:px-8"
              items={[
                { label: 'All collections', url: '/products' },
                {
                  label: collection.category.name,
                  url: `/collections/${collection.category.permalink}/category`
                },
                { label: collection.name + ' Stock Footage' }
              ]}
            />

            <div className="flex flex-col gap-8 lg:flex-row md:px-8 4k:gap-16">
              <div className="lg:w-1/2 max-w-[800px] product-gallery-section mb-2 xl:mb-8">
                {productMediaData(collection) && (
                  <CustomCarouselWithThumbs
                    carouselOptions={carouselOptions}
                    content={productMediaData(collection)}
                  />
                )}
                {!productMediaData(collection) && (
                  <>
                    {collection.featured_video?.length > 0 ? (
                      <YoutubeEmbed
                        embedId={collection.featured_video}
                        className="rounded-none md:rounded-[5px]"
                        autoplay={false}
                      />
                    ) : (
                      <img
                        className="rounded-[8px] w-full"
                        src={collection.media.image}
                        alt={collection.name}
                        onError={(e) => {
                          const target = e.target as Element;
                          target.setAttribute('src', 'https://placehold.co/600x338');
                        }}
                      />
                    )}

                    <div className="grid grid-cols-5 gap-4 mx-6 mt-6 md:mx-0">
                      {collection.demo_video.map((v: CollectionDemoVideoResponse) => (
                        <div className="relative shadow-md" key={v.video_key}>
                          <img
                            src={`https://img.youtube.com/vi/${v.video_key}/hqdefault.jpg`}
                            alt={v.title}
                            className="w-full rounded-[5px]"
                          />
                          <div className="absolute top-0 flex items-center justify-center w-full h-full">
                            <PlayIcon className="w-[48px]" />
                            <PlayIconActive
                              onClick={() => setVideoModal(v)}
                              className="w-[48px] absolute opacity-0 hover:opacity-100 ease-linear transition-all duration-200 cursor-pointer"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div id="product-features-section" className="px-6 md:px-0 lg:w-1/2 4k:pr-12">
                <div className="font-[Inter] text-24 md:text-40 leading-44 font-bold pb-4 sm:pb-0 md:mb-6">
                    <span className='pr-4'>{collection.name}</span>
                    <TagContainer
                      showTooltip={true}
                      popular={collection.is_popular}
                      newRelease={collection.new}
                      varietyPack={collection.variety_pack}
                      free={collection.free_product}
                      membershipTier={collection.membership_tier}
                      containerClasses="big inline-flex align-middle"
                      isCollectionPage={true}
                      forEssentials={true}
                      essentialsMonthlyExpiryDate={collection.ec_schedule_end_at}
                    />
                    <TagContainer
                      showTooltip={true}
                      popular={collection.is_popular}
                      newRelease={collection.new}
                      varietyPack={collection.variety_pack}
                      free={collection.free_product}
                      membershipTier={collection.membership_tier}
                      containerClasses="big inline-flex align-middle"
                      isCollectionPage={true}
                    />

                    {collection?.variety_pack && (
                      <>
                        <QuestionIcon
                        className="inline-flex w-4 scale-[140%] cursor-pointer"
                          data-tooltip-id="variety"
                        />
                        <Tooltip
                          id="variety"
                          className="max-w-[260px] z-40 !text-[14px] !normal-case font-light leading-[22px]"
                          opacity={1}
                          style={{ backgroundColor: 'black' }}>
                          Variety Packs are curated collections made up of assets chosen from
                          multiple collections in our library. Perfect for those who want a little
                          bit of many products.
                        </Tooltip>
                      </>
                    )}

                    {/* IF CONTRIBUTOR MOBILE  */}
                    {collection.contributor && (
                      <Contributor
                        username={collection.contributor?.username}
                        name={collection.contributor?.name}
                        mobile={true}
                        assetCount={collection.elements}
                      />
                    )}
                </div>

                <p className="text-a-gray text-[14px] lg:text-[14px] xl:text-[16px] leading-[24px] font-normal pb-4 whitespace-pre-line">
                  <TruncatedText text={collection.description} />
                </p>

                {!collection.variety_pack && <ProductSpecs collection={collection} />}

                {collection.variety_pack && (
                  <div className="py-4">
                    <h3 className="inter text-[16px] font-semibold">Contains Elements From:</h3>
                    <div className="flex flex-wrap items-center gap-3 mt-3">
                      {collection?.elements_from?.map(
                        (c: CollectionElementsFromResponse, k: number) => (
                          <a
                            href={`/collections/${c.permalink}`}
                            className="bg-white-5 text-gray-500 py-1 px-4 rounded-[5px] text-[14px]"
                            key={k}>
                            {c.name}
                          </a>
                        )
                      )}
                    </div>
                  </div>
                )}

                {
                  collection.active &&
                  <div className="block my-3 mt-4 md:hidden">
                    <AddToWishlistButton popoverClassName="max-md:w-[calc(100vw-48px)]" type="button" product={collection} />
                  </div>
                }
              </div>
            </div>

            {/* MOBILE SIDEBAR*/}
            <div className="block my-8 md:hidden">
              <InactiveCollectionMarker collection={collection} />
              {
                collection.active &&
                <div className="p-6 bg-[#1C1F20]">
                    {!shouldHideAddToCart(collection) && <MiniLicenseProfile setLicenseSelectionAssistDialogOpen={setLicenseSelectionAssistDialogOpen} collectionOwned={ownership} />}

                  {showPricing && (
                    <>
                      <PriceBlock purchasedItem={collection} onPurchased={() => setCollectionOwned(true)} onOwnershipDataLoaded={setOwnership}/>
                        {!shouldHideAddToCart(collection, subscription) &&
                          <>
                            <DividerWithText />
                            <NonSubPriceBlock collection={collection} className="mt-[20px]" onPurchased={() => setCollectionOwned(true)} onOwnershipDataLoaded={setOwnership} />
                          </>
                        }
                        {subscription && shouldHideAddToCart(collection, subscription) && (
                          <div onClick={handlePurchaseOptionsClick} className='font-light text-center underline capitalize cursor-pointer text-a-blue'>Permanent Purchase Options</div>
                        )}
                        <PermanentPurchaseOptionsModal isOpen={openPernamentPaymentOptionsModal} onClose={onClose} collection={collection} />
                    </>
                  )}
                  {showOwnership && (
                    <CollectionOwnership
                      collection={collection}
                      ownership={ownership}
                      products={products}
                    />
                  )}
                </div>
              }
            </div>

            <div className="p-4 md:px-8">
              {
                collection.active &&
                <>
                  <div className="md:flex gap-4 align-middle items-center h-[64px]">
                    <h3 className="font-bold text-[18px] md:text-[20px] inter">
                      {collection.elements} Assets
                    </h3>
                    <p className="text-a-light-gray text-[12px] font-medium">
                      Assets may be purchased individually using credits
                    </p>
                  </div>

                  <div className="py-4">
                    <ProductGrid
                      products={products}
                      collection={collection}
                      selectedProduct={product}
                      ownership={ownership}
                    />
                  </div>
                </>
              }
              <RelatedCollectionsSection collectionId={collection.id} />
              {!subscription && <section className='py-12'>
                <StaticAdContainer />
              </section>}
            </div>
          </div>

          <Sidebar
            collection={collection}
            setCollection={setCollection}
            licenseeProfile={licenseeProfile}
            setLicenseSelectionAssistDialogOpen={setLicenseSelectionAssistDialogOpen}
            collectionOwned={collectionOwned}
            setCollectionOwned={setCollectionOwned}
            setOwnership={setOwnership}
            ownership={ownership}
            products={products}
            setIsOpen={setIsOpen}
            auth={auth}
            totalCreditPrice={totalCreditPrice}
          />
        </div>

      </div>

      {videoModal && (
        <Modal
          classNames={{ modal: '!p-0 !bg-transparent !m-0 w-[100%] lg:w-[800px]' }}
          open={videoModal != null}
          onClose={() => setVideoModal(null)}
          center
          showCloseIcon={false}>
          <YoutubeEmbed embedId={videoModal.video_key} className="bg-black" />
        </Modal>
      )}

      {collection.membership_tier === 'silver' && <DownloadLimitNotification />}
      {collection.free_product && <DownloadLimitNotification />}
      <SubscriptionUpgradeModal />
      {licenseSelectionAssistDialogOpen === true && (
        <LicenseSelectionAssistDialog
          dialogIsOpen={licenseSelectionAssistDialogOpen}
          setDialogIsOpen={setLicenseSelectionAssistDialogOpen}
        />
      )}
    </>
  );
}
